import { useMemo, useState } from 'react';
import { Link, useLocation } from 'wouter';
import Header from '@/components/layout/header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, AlertCircle, LayoutTemplate, Globe, Building } from 'lucide-react';
import {
  useActivationTemplates,
  useDeleteActivationTemplate,
} from '@/hooks/useActivationTemplates';
import {
  ACTIVATION_TYPE_LABELS,
  ACTIVATION_FAMILY_LABELS,
} from '@/types/activations';
import type { ActivationTemplateResponse } from '@/types/generated/activations.generated';

export default function ActivationTemplatesPage() {
  const [, navigate] = useLocation();
  const { data: templates, isLoading, isError, error, refetch } = useActivationTemplates();
  const deleteMutation = useDeleteActivationTemplate();
  const [search, setSearch] = useState('');
  const [toDelete, setToDelete] = useState<ActivationTemplateResponse | null>(null);

  const filtered = useMemo(() => {
    const list = templates ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (t) =>
        (t.name ?? '').toLowerCase().includes(q) ||
        ACTIVATION_TYPE_LABELS[t.activationType].toLowerCase().includes(q),
    );
  }, [templates, search]);

  const confirmDelete = async () => {
    if (!toDelete) return;
    await deleteMutation.mutateAsync(toDelete.id);
    setToDelete(null);
  };

  return (
    <div className="flex-1" data-testid="activation-templates-page">
      <Header
        title="Activation Templates"
        description="Author global and org-custom activation recipes and their customization schema."
      >
        <Button onClick={() => navigate('/activations/templates/new')} data-testid="create-template-button">
          <Plus className="mr-2 h-4 w-4" />
          New template
        </Button>
      </Header>

      <div className="p-6">
        <div className="mb-4 max-w-sm">
          <Input
            placeholder="Search templates…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="template-search"
          />
        </div>

        {isLoading && (
          <div className="space-y-2" data-testid="templates-loading">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        )}

        {isError && (
          <Alert variant="destructive" data-testid="templates-error">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to load templates</AlertTitle>
            <AlertDescription className="flex items-center gap-3">
              {(error as Error)?.message || 'Something went wrong.'}
              <Button size="sm" variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <Card className="flex flex-col items-center gap-3 p-12 text-center" data-testid="templates-empty">
            <LayoutTemplate className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {search ? 'No templates match your search.' : 'No activation templates yet.'}
            </p>
            {!search && (
              <Button onClick={() => navigate('/activations/templates/new')}>
                <Plus className="mr-2 h-4 w-4" />
                Create your first template
              </Button>
            )}
          </Card>
        )}

        {!isLoading && !isError && filtered.length > 0 && (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Family</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead className="w-[120px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t) => (
                  <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                    <TableCell className="font-medium">
                      <Link
                        href={`/activations/templates/${t.id}`}
                        className="hover:underline"
                        data-testid={`template-link-${t.id}`}
                      >
                        {t.name}
                      </Link>
                      {t.nameAr && <span className="ml-2 text-xs text-muted-foreground">{t.nameAr}</span>}
                    </TableCell>
                    <TableCell>{ACTIVATION_TYPE_LABELS[t.activationType]}</TableCell>
                    <TableCell>{ACTIVATION_FAMILY_LABELS[t.activationFamily]}</TableCell>
                    <TableCell>
                      {t.isGlobal ? (
                        <Badge variant="secondary" className="gap-1">
                          <Globe className="h-3 w-3" /> Global
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1">
                          <Building className="h-3 w-3" /> Org-custom
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit template"
                        onClick={() => navigate(`/activations/templates/${t.id}`)}
                        data-testid={`edit-template-${t.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete template"
                        onClick={() => setToDelete(t)}
                        data-testid={`delete-template-${t.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-template-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>{toDelete?.name}</strong>. Templates referenced by an
              activation cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              data-testid="confirm-delete-template"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
